import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Checkbox,
  Button
} from '@mui/material';
import { Copy, Download } from 'lucide-react';
import { 
  InvoiceStatus, 
  InvoiceClassification, 
  InvoiceField 
} from '../../config/invoice-config';
import { UserRole } from '../../config/workflow-config';
import { useWorkflowPermissions } from '../../hooks/useWorkflowPermissions';
import { ChevronStatusFilter } from '../../components/molecules/ChevronStatusFilter';

interface WorkflowUser {
  id: string;
  roles: UserRole[];
  isAdmin?: boolean;
}

export const AdminInvoiceWorkflowTab: React.FC = () => {
  // State for filters
  const [selectedRole] = useState<UserRole>(UserRole.ADMINISTRATOR);
  const [selectedClassification] = useState<InvoiceClassification>(InvoiceClassification.DELIVER_DIRECT);
  const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus>(InvoiceStatus.UNPROCESSED);
  
  // State for JSON dialog
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [generatedJson, setGeneratedJson] = useState<string>('');
  
  // State for checkbox permissions
  const [checkboxPermissions, setCheckboxPermissions] = useState<Record<string, Record<string, { visible: boolean; editable: boolean }>>>({});

  // Mock user based on selected role
  const mockUser: WorkflowUser = useMemo(() => {
    return {
      id: 'admin-user',
      roles: [selectedRole],
      isAdmin: selectedRole === UserRole.ADMINISTRATOR
    };
  }, [selectedRole]);

  // Get workflow permissions
  const permissions = useWorkflowPermissions(
    selectedStatus,
    selectedClassification,
    selectedRole,
    mockUser.id,
    undefined // assignedToUserId not available in admin tab
  );



  // Helper function to get status display name
  const getStatusDisplayName = (status: InvoiceStatus): string => {
    const statusNames: Record<InvoiceStatus, string> = {
      [InvoiceStatus.UNPROCESSED]: '1 - Unprocessed',
      [InvoiceStatus.ADDING]: '2 - Adding',
      [InvoiceStatus.NEW_PRODUCTS]: '3 - New Products',
      [InvoiceStatus.IR]: '4 - IR',
      [InvoiceStatus.INVOICING]: '5 - Invoicing',
      [InvoiceStatus.HEAD_OFFICE_STOCK]: '6 - Head Office Stock',
      [InvoiceStatus.EXPENSE_INVOICES]: '7 - Expense Invoices',
      [InvoiceStatus.ENTERING_STOCK]: '8 - Entering Stock',
      [InvoiceStatus.ENTERING_EXPENSE]: '9 - Entering Expense',
      [InvoiceStatus.CREDIT_NOTE]: '10 - Credit Note',
      [InvoiceStatus.CREDIT_ENTERING]: '11 - Credit Entering',
      [InvoiceStatus.COMPLETED]: '12 - Completed'
    };
    return statusNames[status] || `Status ${status}`;
  };

  // Helper function to get classification display name
  const getClassificationDisplayName = (classification: InvoiceClassification): string => {
    const classificationNames: Record<InvoiceClassification, string> = {
      [InvoiceClassification.DELIVER_DIRECT]: 'Deliver Direct',
      [InvoiceClassification.HEAD_OFFICE]: 'Head Office',
      [InvoiceClassification.EXPENSE]: 'Expense',
      [InvoiceClassification.CREDIT_NOTE]: 'Credit Note'
    };
    return classificationNames[classification];
  };

  // Get all available fields in specific order
  const allFields = [
    InvoiceField.CLASSIFICATION,
    InvoiceField.NEW_STOCK,
    InvoiceField.PRODUCT_ADDED,
    InvoiceField.SUPPLIER,
    InvoiceField.INVOICE_NUMBER,
    InvoiceField.INVOICE_DATE,
    InvoiceField.DUE_DATE,
    InvoiceField.TOTAL_AMOUNT,
    InvoiceField.SUPPLIER_ABN,
    InvoiceField.STORE_ID,
    InvoiceField.PO,
    InvoiceField.SO,
    InvoiceField.IR,
    InvoiceField.INV,
    InvoiceField.CLAIM,
    InvoiceField.CN,
    InvoiceField.CREDIT_AMOUNT,
    InvoiceField.NOTES
  ];

  // Initialize checkbox permissions based on current status
  const initializeCheckboxPermissions = useCallback(() => {
    const newPermissions: Record<string, Record<string, { visible: boolean; editable: boolean }>> = {};
    
    if ((selectedStatus === InvoiceStatus.UNPROCESSED || selectedStatus.toString() === "unprocessed") && 
        (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for unprocessed + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: true },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: true },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: false, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: false, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: false, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: false, editable: false },
        [InvoiceField.DUE_DATE]: { visible: false, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: false, editable: false },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: false, editable: false }
            };
    } else if ((selectedStatus === InvoiceStatus.ADDING || selectedStatus.toString() === "adding") && 
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for adding + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: true, editable: true },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: true, editable: true },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: true, editable: true },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.NEW_PRODUCTS || selectedStatus.toString() === "new_products") && 
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for new_products + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: true, editable: false },
        [InvoiceField.PO]: { visible: true, editable: false },
        [InvoiceField.SO]: { visible: true, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.IR || selectedStatus.toString() === "ir") && 
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for ir + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: true, editable: false },
        [InvoiceField.PO]: { visible: true, editable: false },
        [InvoiceField.SO]: { visible: true, editable: false },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.INVOICING || selectedStatus.toString() === "invoicing") && 
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for invoicing + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: true, editable: true },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: true, editable: true },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: true, editable: false },
        [InvoiceField.PO]: { visible: true, editable: false },
        [InvoiceField.SO]: { visible: true, editable: false },
        [InvoiceField.IR]: { visible: true, editable: false },
        [InvoiceField.INV]: { visible: true, editable: true },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
          } else if ((selectedStatus === InvoiceStatus.HEAD_OFFICE_STOCK || selectedStatus.toString() === "head_office_stock") && 
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for head_office_stock + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: true, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.EXPENSE_INVOICES || selectedStatus.toString() === "expense_invoices") &&
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for expense_invoices + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.ENTERING_STOCK || selectedStatus.toString() === "entering_stock") &&
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for entering_stock + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: true, editable: true },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: true, editable: true },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: true, editable: false },
        [InvoiceField.PO]: { visible: true, editable: false },
        [InvoiceField.SO]: { visible: true, editable: false },
        [InvoiceField.IR]: { visible: true, editable: false },
        [InvoiceField.INV]: { visible: true, editable: true },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.ENTERING_EXPENSE || selectedStatus.toString() === "entering_expense") &&
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for entering_expense + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: false, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: false, editable: false },
        [InvoiceField.CN]: { visible: false, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: false, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.CREDIT_NOTE || selectedStatus.toString() === "credit_note") &&
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for credit_note + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: true, editable: true },
        [InvoiceField.CN]: { visible: true, editable: true },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: true, editable: true },
        [InvoiceField.CN]: { visible: true, editable: true },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: true, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: true, editable: false },
        [InvoiceField.CN]: { visible: true, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.CREDIT_ENTERING || selectedStatus.toString() === "credit_entering") &&
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for credit_entering + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: true, editable: true },
        [InvoiceField.CN]: { visible: true, editable: true },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: true, editable: true },
        [InvoiceField.CN]: { visible: true, editable: true },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: true, editable: false },
        [InvoiceField.PO]: { visible: false, editable: false },
        [InvoiceField.SO]: { visible: false, editable: false },
        [InvoiceField.IR]: { visible: false, editable: false },
        [InvoiceField.INV]: { visible: false, editable: false },
        [InvoiceField.CLAIM]: { visible: true, editable: false },
        [InvoiceField.CN]: { visible: true, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else if ((selectedStatus === InvoiceStatus.COMPLETED || selectedStatus.toString() === "completed") &&
               (selectedClassification === InvoiceClassification.DELIVER_DIRECT || selectedClassification.toString() === "deliver_direct")) {
      // Lock in the exact configuration for completed + deliver_direct
      newPermissions[UserRole.ADMINISTRATOR] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: true, editable: true },
        [InvoiceField.CLAIM]: { visible: true, editable: true },
        [InvoiceField.CN]: { visible: true, editable: true },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.FINANCE] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: true },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: true },
        [InvoiceField.DUE_DATE]: { visible: true, editable: true },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: true },
        [InvoiceField.STORE_ID]: { visible: true, editable: true },
        [InvoiceField.PO]: { visible: true, editable: true },
        [InvoiceField.SO]: { visible: true, editable: true },
        [InvoiceField.IR]: { visible: true, editable: true },
        [InvoiceField.INV]: { visible: true, editable: true },
        [InvoiceField.CLAIM]: { visible: true, editable: true },
        [InvoiceField.CN]: { visible: true, editable: true },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: true },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
      
      newPermissions[UserRole.STOCK] = {
        [InvoiceField.CLASSIFICATION]: { visible: true, editable: false },
        [InvoiceField.NEW_STOCK]: { visible: false, editable: false },
        [InvoiceField.PRODUCT_ADDED]: { visible: false, editable: false },
        [InvoiceField.SUPPLIER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_NUMBER]: { visible: true, editable: false },
        [InvoiceField.INVOICE_DATE]: { visible: true, editable: false },
        [InvoiceField.DUE_DATE]: { visible: true, editable: false },
        [InvoiceField.TOTAL_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.SUPPLIER_ABN]: { visible: true, editable: false },
        [InvoiceField.STORE_ID]: { visible: true, editable: false },
        [InvoiceField.PO]: { visible: true, editable: false },
        [InvoiceField.SO]: { visible: true, editable: false },
        [InvoiceField.IR]: { visible: true, editable: false },
        [InvoiceField.INV]: { visible: true, editable: false },
        [InvoiceField.CLAIM]: { visible: true, editable: false },
        [InvoiceField.CN]: { visible: true, editable: false },
        [InvoiceField.CREDIT_AMOUNT]: { visible: true, editable: false },
        [InvoiceField.NOTES]: { visible: true, editable: true }
      };
    } else {
      // Default initialization for other statuses/classifications with advanced defaults
      Object.values(UserRole).forEach(role => {
        newPermissions[role] = {};
        
        allFields.forEach(field => {
          let visible = false;
          let editable = false;

          // Advanced Defaults - Apply to all statuses
          if (field === InvoiceField.NOTES) {
            // Notes are always visible and editable for all roles
            visible = true;
            editable = true;
          } else if (field === InvoiceField.CLASSIFICATION) {
            // Classification is always visible but only editable in unprocessed status
            visible = true;
            editable = false; // Read-only for all other statuses
          } else if (field === InvoiceField.SUPPLIER || 
                     field === InvoiceField.INVOICE_NUMBER || 
                     field === InvoiceField.INVOICE_DATE || 
                     field === InvoiceField.DUE_DATE || 
                     field === InvoiceField.TOTAL_AMOUNT || 
                     field === InvoiceField.SUPPLIER_ABN) {
            // Core invoice fields: always visible, editable for admin/finance, read-only for stock
            visible = true; // All roles can see these fields
            editable = role === UserRole.ADMINISTRATOR || role === UserRole.FINANCE; // Only admin/finance can edit
          } else if (field === InvoiceField.STORE_ID || 
                     field === InvoiceField.PO || 
                     field === InvoiceField.SO) {
            // Store, PO, SO are usually visible to all but editable based on role
            visible = true;
            editable = role === UserRole.ADMINISTRATOR || role === UserRole.FINANCE;
          } else {
            // Other fields default to hidden
            visible = false;
            editable = false;
          }

          newPermissions[role][field] = { visible, editable };
        });
      });
    }
    
    setCheckboxPermissions(newPermissions);
  }, [selectedStatus, selectedClassification]);

  // Initialize permissions when status or classification changes
  useEffect(() => {
    initializeCheckboxPermissions();
  }, [initializeCheckboxPermissions]);

  // Handle checkbox changes
  const handleCheckboxChange = (field: string, role: string, permissionType: 'visible' | 'editable') => {
    setCheckboxPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [field]: {
          ...prev[role]?.[field],
          [permissionType]: !prev[role]?.[field]?.[permissionType]
        }
      }
    }));
  };

  // Generate JSON configuration for current status
  const generateStatusConfig = useCallback(() => {
    const config = {
      status: selectedStatus.toString().toLowerCase(),
      statusName: getStatusDisplayName(selectedStatus),
      classification: selectedClassification.toString().toLowerCase(),
      classificationName: getClassificationDisplayName(selectedClassification),
      fieldPermissions: {} as Record<string, any>
    };

    // Use current checkbox permissions
    Object.values(UserRole).forEach(role => {
      const roleKey = role === UserRole.ADMINISTRATOR ? 'Administrator' : 
                     role === UserRole.FINANCE ? 'Finance' : 'Stock';
      
      config.fieldPermissions[roleKey] = {};
      
      allFields.forEach(field => {
        const permissions = checkboxPermissions[role]?.[field] || { visible: false, editable: false };
        // Convert field names to lowercase with underscores to match your JSON format
        const fieldKey = field.toString().toLowerCase();
        config.fieldPermissions[roleKey][fieldKey] = {
          visible: permissions.visible,
          editable: permissions.editable
        };
      });
    });

    return config;
  }, [selectedStatus, selectedClassification, checkboxPermissions]);

  // Force JSON generation when component mounts or permissions change
  useEffect(() => {
    // Force generate JSON even if permissions are empty
    const config = generateStatusConfig();
    const jsonString = JSON.stringify(config, null, 2);
    setGeneratedJson(jsonString);
  }, [selectedStatus, selectedClassification, checkboxPermissions, generateStatusConfig]);

  // Handle JSON generation and display
  const handleGenerateJson = () => {
    const config = generateStatusConfig();
    const jsonString = JSON.stringify(config, null, 2);
    setGeneratedJson(jsonString);
    setJsonDialogOpen(true);
  };

  // Copy JSON to clipboard
  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(generatedJson);
      // You could add a toast notification here
    } catch (err) {
      // Failed to copy JSON - silently handle
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>
        Invoice Workflow Configuration
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Test and configure field permissions for different workflow states, user roles, and invoice classifications.
      </Typography>

      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Workflow Status Filter
        </Typography>
        
        <ChevronStatusFilter
          selectedStatus={selectedStatus}
          onStatusChange={(status) => {
            setSelectedStatus(status as InvoiceStatus);
          }}
          showAll={false}
          title=""
          className=""
        />
        
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              const config = generateStatusConfig();
              const jsonString = JSON.stringify(config, null, 2);
              setGeneratedJson(jsonString);
            }}
            size="small"
          >
            Refresh JSON
          </Button>
          <Typography variant="body2" color="text.secondary">
            Current JSON length: {generatedJson.length} characters
          </Typography>
        </Box>
      </Paper>



      {/* Field Permissions Table */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Field Permissions: {getStatusDisplayName(selectedStatus)}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleGenerateJson}
            startIcon={<Download />}
            size="small"
          >
            Generate JSON Config
          </Button>
        </Box>
        
        <TableContainer>
          <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>Field</TableCell>
                <TableCell align="center" colSpan={2} sx={{ border: '1px solid #e0e0e0' }}>Administrator</TableCell>
                <TableCell align="center" colSpan={2} sx={{ border: '1px solid #e0e0e0' }}>Finance</TableCell>
                <TableCell align="center" colSpan={2} sx={{ border: '1px solid #e0e0e0' }}>Stock</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}></TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>Visibility</TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>Editable</TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>Visibility</TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>Editable</TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>Visibility</TableCell>
                <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>Editable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allFields.map((field) => {
                const adminPermissions = checkboxPermissions[UserRole.ADMINISTRATOR]?.[field] || { visible: false, editable: false };
                const financePermissions = checkboxPermissions[UserRole.FINANCE]?.[field] || { visible: false, editable: false };
                const stockPermissions = checkboxPermissions[UserRole.STOCK]?.[field] || { visible: false, editable: false };
                
                return (
                  <TableRow key={field} hover>
                    <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" fontWeight="medium">
                        {field.replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    {/* Administrator Permissions */}
                    <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                      <Checkbox
                        checked={adminPermissions.visible}
                        color="primary"
                        onChange={() => handleCheckboxChange(field, UserRole.ADMINISTRATOR, 'visible')}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                      <Checkbox
                        checked={adminPermissions.editable}
                        color="primary"
                        onChange={() => handleCheckboxChange(field, UserRole.ADMINISTRATOR, 'editable')}
                      />
                    </TableCell>
                    {/* Finance Permissions */}
                    <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                      <Checkbox
                        checked={financePermissions.visible}
                        color="primary"
                        onChange={() => handleCheckboxChange(field, UserRole.FINANCE, 'visible')}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                      <Checkbox
                        checked={financePermissions.editable}
                        color="primary"
                        onChange={() => handleCheckboxChange(field, UserRole.FINANCE, 'editable')}
                      />
                    </TableCell>
                    {/* Stock Permissions */}
                    <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                      <Checkbox
                        checked={stockPermissions.visible}
                        color="primary"
                        onChange={() => handleCheckboxChange(field, UserRole.STOCK, 'visible')}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ border: '1px solid #e0e0e0' }}>
                      <Checkbox
                        checked={stockPermissions.editable}
                        color="primary"
                        onChange={() => handleCheckboxChange(field, UserRole.STOCK, 'editable')}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Current JSON Display */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current JSON Configuration
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Status: {selectedStatus} | Classification: {selectedClassification}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleCopyJson}
            startIcon={<Copy />}
            size="small"
          >
            Copy JSON
          </Button>
        </Box>
        
        <Box sx={{ 
          backgroundColor: '#f5f5f5', 
          p: 2, 
          borderRadius: 1, 
          border: '1px solid #e0e0e0',
          maxHeight: '300px',
          overflow: 'auto'
        }}>
          <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
            {generatedJson || 'No JSON generated yet...'}
          </pre>
        </Box>
      </Paper>

      {/* Debug Information */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Debug Information
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">User Roles</Typography>
            <Box sx={{ mt: 1 }}>
              {permissions.debug.userRoles.map((role) => (
                <Chip key={role} label={role} size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          </Box>
          
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Visible Fields</Typography>
            <Box sx={{ mt: 1 }}>
              {permissions.debug.visibleFields.map((field) => (
                <Chip key={field} label={field.replace(/_/g, ' ')} size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          </Box>
          
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Editable Fields</Typography>
            <Box sx={{ mt: 1 }}>
              {permissions.debug.editableFields.map((field) => (
                <Chip key={field} label={field.replace(/_/g, ' ')} size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          </Box>
          
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Required Fields</Typography>
            <Box sx={{ mt: 1 }}>
              {permissions.debug.requiredFields.map((field) => (
                <Chip key={field} label={field.replace(/_/g, ' ')} size="small" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Special Rules Alert for Unprocessed Status */}
      {selectedStatus === InvoiceStatus.UNPROCESSED && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Special Rules for Unprocessed Status:
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>Admin & Finance:</strong> Can edit Classification, Supplier, Invoice Number, Date, Due Date, Total Amount, Supplier ABN
            <br />
            • <strong>All Roles:</strong> Can edit Notes
            <br />
            • <strong>Hidden for All:</strong> New Stock, Product Added, Store, PO, SO, IR, INV, Claim, CN, Credit Amount
          </Typography>
        </Alert>
      )}

      {/* JSON Configuration Dialog */}
      <Dialog 
        open={jsonDialogOpen} 
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              JSON Configuration
            </Typography>
            <Box>
              <Tooltip title="Copy to Clipboard">
                <IconButton onClick={handleCopyJson} size="small">
                  <Copy />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Use this JSON configuration for other workflow statuses. Click any permission chip in the table above to see field-specific configurations.
          </Typography>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              backgroundColor: '#f5f5f5', 
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              maxHeight: '400px',
              overflow: 'auto'
            }}
          >
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {generatedJson}
            </pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
